/**
 * Validation utility functions for email, phone, and password strength
 */

/** Matches typical “strong password” guidance; length ≥ 8 satisfies backend (@Size min 6). */
export type PasswordRuleStatus = {
  minLength: boolean;
  hasUpper: boolean;
  hasLower: boolean;
  hasDigit: boolean;
  hasSpecial: boolean;
};

export const PASSWORD_REQUIREMENTS_HINT =
  'Use at least 8 characters with uppercase and lowercase letters, a number, and a symbol (e.g. !@#$%).';

export function getPasswordRuleStatus(password: string): PasswordRuleStatus {
  return {
    minLength: password.length >= 8,
    hasUpper: /[A-Z]/.test(password),
    hasLower: /[a-z]/.test(password),
    hasDigit: /\d/.test(password),
    hasSpecial: /[^A-Za-z0-9]/.test(password),
  };
}

export function isPasswordStrong(password: string): boolean {
  const r = getPasswordRuleStatus(password);
  return r.minLength && r.hasUpper && r.hasLower && r.hasDigit && r.hasSpecial;
}

/**
 * @returns Empty string if the password meets strength rules; otherwise a short error message.
 */
export function getPasswordErrorMessage(password: string): string {
  if (!password || password.trim() === '') {
    return 'Password is required';
  }
  if (!isPasswordStrong(password)) {
    return 'Please create a stronger password that meets every requirement below.';
  }
  return '';
}

/** Shown near “new password” fields; backend enforces the rule for change + forgot-password reset. */
export const PASSWORD_NO_RECENT_REUSE_HINT =
  'Your new password cannot be the same as your current password or either of your last two passwords.';

/**
 * Validates email address format
 * @param email - Email address to validate
 * @returns true if email is valid, false otherwise
 */
export const isValidEmail = (email: string): boolean => {
  if (!email || email.trim() === '') {
    return false;
  }
  
  // Email regex pattern
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  
  // Check basic format
  if (!emailRegex.test(email)) {
    return false;
  }
  
  // Check for Gmail specifically (if needed)
  const emailLower = email.toLowerCase();
  if (emailLower.includes('@gmail.com')) {
    // Gmail validation: username should be valid
    const gmailRegex = /^[a-z0-9]([a-z0-9._-]*[a-z0-9])?@gmail\.com$/i;
    return gmailRegex.test(email);
  }
  
  return true;
};

/** Digits only; if more than 10, keeps the last 10 (matches API / rn-app). */
export function normalizeTenDigitPhone(raw: string): string {
  const digits = (raw ?? '').replace(/\D/g, '');
  if (digits.length > 10) return digits.slice(-10);
  return digits;
}

/** Account / business mobile: exactly 10 digits after normalization. */
export const isValidPhoneNumber = (phoneNumber: string): boolean => {
  return normalizeTenDigitPhone(phoneNumber).length === 10;
};

/**
 * Gets error message for email validation
 * @param email - Email address to validate
 * @returns Error message if invalid, empty string if valid
 */
export const getEmailErrorMessage = (email: string): string => {
  if (!email || email.trim() === '') {
    return 'Email is required';
  }
  
  if (!isValidEmail(email)) {
    if (email.toLowerCase().includes('@gmail.com')) {
      return 'Please enter a valid Gmail address (e.g., example@gmail.com)';
    }
    return 'Please enter a valid email address (e.g., example@domain.com)';
  }
  
  return '';
};

/**
 * Gets error message for phone number validation
 * @param phoneNumber - Phone number to validate
 * @returns Error message if invalid, empty string if valid
 */
export const getPhoneNumberErrorMessage = (phoneNumber: string): string => {
  if (!phoneNumber || phoneNumber.trim() === '') {
    return 'Phone number is required';
  }

  if (!isValidPhoneNumber(phoneNumber)) {
    return 'Please enter a valid 10-digit mobile number (digits only).';
  }

  return '';
};

/** Login field: email OR 10-digit mobile (aligned with `/api/auth/signin`). */
export function formatLoginIdentifierInput(raw: string): string {
  const t = raw ?? '';
  if (t.includes('@')) {
    return t.replace(/\s+/g, ' ').replace(/^\s+/, '');
  }
  return t.replace(/\D/g, '').slice(0, 10);
}

export function getLoginIdentifierValidationError(raw: string): string {
  const t = raw.trim();
  if (!t) {
    return 'Please enter your email or mobile number.';
  }
  if (t.includes('@')) {
    return getEmailErrorMessage(t);
  }
  const n = normalizeTenDigitPhone(t);
  if (n.length !== 10) {
    return 'Mobile number must be exactly 10 digits.';
  }
  return '';
}
